import updateDataLayer from '@utils/helpers/gtm';
import useAriaLabels from '@utils/hooks/useAriaLabels';
import { useReducedMotion } from 'framer-motion';
import type { PropsWithChildren } from 'react';
import { A11y, Navigation } from 'swiper/modules';

import { Swiper, type SwiperProps } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/a11y';
import 'swiper/css/autoplay';
import 'swiper/css/effect-fade';
import 'swiper/css/keyboard';
import type { SwiperModule } from 'swiper/types';

interface Props extends SwiperProps {
  readonly additionalModules?: SwiperModule[];
  readonly className?: string;
  readonly disableDefaultSlideAriaLabel?: boolean;
  readonly id?: string;
  readonly navId?: string;
  readonly analyticsEnabled?: boolean;
  readonly trackingContentName?: string;
  readonly onSetIsSwiperBeginning?: (isSwiperBeginning: boolean) => void;
  readonly onSetIsSwiperEnd?: (isSwiperEnd: boolean) => void;
}

const Carousel = ({
  additionalModules = [],
  analyticsEnabled = false,
  a11y: a11yProps,
  children,
  className,
  disableDefaultSlideAriaLabel,
  id = 'carousel',
  navId = '',
  trackingContentName,
  onSetIsSwiperBeginning,
  onSetIsSwiperEnd,
  onResize,
  ...props
}: PropsWithChildren<Props>) => {
  const prefersReducedMotion = useReducedMotion();
  const { carouselSlideAriaLabel } = useAriaLabels();

  if (!children) {
    return null;
  }

  return (
    <Swiper
      id={id}
      onSlideChangeTransitionEnd={(swiper) => {
        if (analyticsEnabled && swiper.realIndex !== 0) {
          updateDataLayer({
            event: 'carousel',
            interaction: 'tab_change',
            link_text:
              swiper.previousIndex < swiper.activeIndex ? 'Next' : 'Previous',
            ...(trackingContentName
              ? { content_name: trackingContentName }
              : {}),
          });
        }
      }}
      a11y={{
        enabled: true,
        itemRoleDescriptionMessage: carouselSlideAriaLabel,
        scrollOnFocus: true,
        ...(disableDefaultSlideAriaLabel ? { slideLabelMessage: '' } : {}),
        ...(typeof a11yProps === 'object' ? a11yProps : {}),
      }}
      className={className}
      navigation={{
        enabled: !!navId,
        prevEl: `#prevBtn-${navId}`,
        nextEl: `#nextBtn-${navId}`,
      }}
      modules={[...[A11y, Navigation], ...additionalModules]}
      onResize={(swiper) => {
        onResize?.(swiper);
        onSetIsSwiperBeginning?.(swiper.isBeginning);
        onSetIsSwiperEnd?.(swiper.isEnd);
      }}
      onUpdate={(swiper) => {
        onSetIsSwiperBeginning?.(swiper.isBeginning);
        onSetIsSwiperEnd?.(swiper.isEnd);
      }}
      onReachBeginning={() => {
        onSetIsSwiperBeginning?.(true);
      }}
      onReachEnd={() => {
        onSetIsSwiperEnd?.(true);
      }}
      onFromEdge={(swiper) => {
        onSetIsSwiperBeginning?.(swiper.isBeginning);
        onSetIsSwiperEnd?.(swiper.isEnd);
      }}
      {...(prefersReducedMotion ? { speed: 0 } : {})}
      {...props}
    >
      {children}
    </Swiper>
  );
};

export default Carousel;
